import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function KompositterrassePage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const galleryImages = [
    {
      src: "/_assets/kompositterrasse-hero.webp",
      title: "Moderne kompositterrasse ved villa",
      category: "Kompositterrasse",
      desc: "Skræddersyet terrasse i kompositbrædder med flot træstruktur og rene linjer."
    },
    {
      src: "/_assets/kompositterrasse-have.webp",
      title: "Flerniveau kompositterrasse",
      category: "Have & Uderum",
      desc: "Gennemtænkt opbygning med integrerede trin og niveaudeling mod græsplæne."
    },
    {
      src: "/_assets/glashegn.webp",
      title: "Terrasse med glasafskærmning",
      category: "Afskærmning",
      desc: "Terrasseløsning kombineret med klart læhegn for optimal udnyttelse af uderummet."
    },
    {
      src: "/_assets/komposithegn-laage.webp",
      title: "Komposithegn og låge ved terrasse",
      category: "Afgrænsning",
      desc: "Matchende kompositløsning til afskærmning og privatliv omkring terrassen."
    }
  ];

  const planningPoints = [
    {
      title: "Placeringen i forhold til hus, have og sol",
      desc: "Optimering i forhold til solens gang, lækroge og naturlig adgang fra boligen.",
      icon: "fa-sun"
    },
    {
      title: "Terrassens størrelse og ønskede form",
      desc: "Tilpasning til grundens proportioner og de zoner, du ønsker at etablere.",
      icon: "fa-draw-polygon"
    },
    {
      title: "Underlaget og den nødvendige opbygning",
      desc: "Solid strøopbygning og fundament, der sikrer en stabil terrasse i mange år.",
      icon: "fa-cubes"
    },
    {
      title: "Behov for trin eller forskellige niveauer",
      desc: "Etablering af niveauforskelle for at skabe zoner til spisning, grill og afslapning.",
      icon: "fa-stairs"
    },
    {
      title: "Adgang, afskærmning og sammenhæng med resten af haven",
      desc: "Samspil med hegn, låger, havegange og den øvrige beplantning.",
      icon: "fa-arrows-split-up-and-left"
    }
  ];

  return (
    <>
      <SEO
        title="Kompositterrasse | Hvad koster din terrasse? | Hovendals"
        description="Få en kompositterrasse, der passer til dit uderum og kræver mindre pleje. Hovendals rådgiver om underlag, brædder og pris. Kontakt os her."
        ogImage="/_assets/kompositterrasse-hero.webp"
      />

      {/* Hero Section */}
      <section className="relative min-h-[520px] lg:min-h-[620px] flex items-center bg-[#143463] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl("/_assets/kompositterrasse-hero.webp", "width=1920,quality=85,format=auto")}
            alt="Moderne kompositterrasse tilpasset bolig og have"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#143463]/95 via-[#143463]/75 to-transparent"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 w-full">
          <div className="max-w-2xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 text-[#C2CCDF] text-xs font-semibold uppercase tracking-wider">
              <i className="fa-sharp fa-solid fa-layer-group text-[#004FA4]" aria-hidden="true"></i>
              <span>Uderum & Terrasser</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.1]">
              Få en kompositterrasse tilpasset dit uderum
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 font-light leading-relaxed">
              En kompositterrasse er et oplagt valg, når du ønsker et flot og praktisk terrasseområde med et begrænset behov for løbende vedligeholdelse.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Indhent tilbud</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
                <span>Ring {companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Introduktion og materiale */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Skræddersyet håndværk
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                Få en terrasse i komposit tilpasset dit uderum
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  En kompositterrasse er et oplagt valg, når du ønsker et flot og praktisk terrasseområde med et begrænset behov for løbende vedligeholdelse. Hos Hovendals bygger vi terrasser efter mål og tager udgangspunkt i boligen, haven og den måde, du ønsker at bruge dit uderum på.
                </p>
                <p>
                  Komposit består typisk af en kombination af træfibre og plast. Det giver terrassebrædder med andre egenskaber end traditionelle træbrædder. Søger du efter komposit træ til terrasse, er det derfor vigtigt at være opmærksom på, at der findes forskellige typer og kvaliteter. Vi hjælper med at finde en løsning, der passer til både projektet og dine ønsker.
                </p>
                <p>
                  Vil du i stedet have det naturlige materiale og udtryk fra traditionelle brædder, kan du se nærmere på vores <Link to="/traeterrasse" className="text-[#004FA4] font-medium underline hover:text-[#143463] transition-colors">træterrasse</Link>.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group"
                >
                  <span>Tag en snak med os om dit projekt</span>
                  <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative border border-[#D6D3CE]/60 p-3 bg-white shadow-lg">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img
                    src={imageUrl("/_assets/kompositterrasse-have.webp", "width=800,quality=85,format=auto")}
                    alt="Kompositterrasse opbygget i haven med rene linjer"
                    className="w-full h-full object-cover hover:scale-102 transition-transform duration-500"
                  />
                </div>
                <div className="p-3 bg-[#F3F4F7] mt-3 flex items-center justify-between text-xs text-[#143463]">
                  <span className="font-medium">Vedligeholdelsesvenlige kompositbrædder</span>
                  <span className="text-[#4B515D]">Specialtilpasset mål</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 2: Hvorfor vælge en kompositterrasse? */}
      <section className="py-20 md:py-24 bg-[#F3F4F7] border-y border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Mindre vedligeholdelse
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight">
              Hvorfor vælge en kompositterrasse?
            </h2>
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              En komposit terrasse kan være interessant, hvis du gerne vil bruge mindre tid på den løbende behandling af terrassebrædderne. Materialet skal stadig rengøres efter behov, men du slipper typisk for den oliebehandling, som mange træterrasser kræver.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-broom" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Vedligeholdelsesvenlig
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Nogle søger efter en vedligeholdelsesfri træterrasse, når målet i virkeligheden er at finde en terrasse, der kræver mindre pleje. Her kan komposit være et relevant alternativ. Det er dog mere præcist at tale om en vedligeholdelsesvenlig terrasse, da udendørs overflader stadig kan få snavs og belægninger og derfor bør rengøres efter behov.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-shield-halved" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Kombiner med hegn
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Vil du også begrænse vedligeholdelsen omkring opholdsområdet, kan terrassen kombineres med et <Link to="/hegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">vedligeholdelsesfrit hegn</Link>.
              </p>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Ønsker du samtidig mere privatliv, kan et <Link to="/komposithegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">komposithegn</Link> skabe en naturlig afskærmning omkring terrassen. Vi tilbyder også <Link to="/betonhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">betonhegn</Link>, hvis det passer mere til dig.
              </p>
            </div>

            <div className="bg-white p-8 border border-[#D6D3CE]/60 space-y-4">
              <div className="w-12 h-12 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center text-xl">
                <i className="fa-sharp fa-solid fa-award" aria-hidden="true"></i>
              </div>
              <h3 className="text-xl font-normal text-[#143463]">
                Kvalitet & holdbarhed
              </h3>
              <p className="text-sm text-[#4B515D] font-light leading-relaxed">
                Kompositbrædder af høj kvalitet modstår det omskiftelige danske vejr uden at slå sig, flække eller splintre. Det giver et sikkert og behageligt underlag til både børn og voksne i mange år.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Terrasse i komposit med plads til dine ønsker */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6 mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Design & Tilpasning
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
              Terrasse i komposit med plads til dine ønsker
            </h2>
            <p className="text-base sm:text-lg text-[#4B515D] font-light leading-relaxed">
              En terrasse i komposit behøver ikke være en enkel firkant foran huset. Formen kan tilpasses bygningen og haven, og der kan arbejdes med forskellige størrelser, retninger og niveauer.
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-lg font-medium text-[#143463] uppercase tracking-wider mb-6">
              Når vi planlægger terrassen, ser vi blandt andet på:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {planningPoints.map((point, idx) => (
                <div key={idx} className="p-6 border border-[#D6D3CE]/60 bg-white hover:border-[#004FA4]/40 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#F3F4F7] text-[#004FA4] flex items-center justify-center shrink-0 text-base">
                      <i className={`fa-sharp fa-solid ${point.icon}`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <h4 className="font-medium text-[#143463] text-sm mb-1.5 leading-snug">
                        {point.title}
                      </h4>
                      <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                        {point.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#F3F4F7] border border-[#D6D3CE]/60 p-8 sm:p-10 space-y-4">
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              På en grund med højdeforskelle kan terrassen eksempelvis opbygges i flere niveauer og skabe separate områder til spisebord, grill og afslapning. Vi ser samtidig på ventilation og afvanding, så konstruktionen passer til forholdene på stedet.
            </p>
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              Skal terrassen afgrænses fra resten af haven, kan et <Link to="/panelhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">panelhegn</Link> være en enkel løsning. Har du allerede et komposithegn, kan en <Link to="/kompositlaage" className="text-[#004FA4] font-medium underline hover:text-[#143463]">kompositlåge</Link> skabe en naturlig adgang til området.
            </p>
          </div>
        </div>
      </section>

      {/* Sektion 4: Hvad koster en kompositterrasse? */}
      <section className="py-20 md:py-24 bg-[#143463] text-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF]">
                Pris & Beregning
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight leading-snug">
                Hvad koster en kompositterrasse?
              </h2>
              <div className="space-y-4 text-slate-200 font-light leading-relaxed text-base">
                <p>
                  En kompositterrasse pris afhænger blandt andet af terrassens størrelse, materialevalget og den nødvendige opbygning. Derfor kan en generel kvadratmeterpris sjældent fortælle præcist, hvad dit projekt kommer til at koste.
                </p>
                <p>
                  Valget af kompositbrædder, underlagets tilstand, terrassens højde og eventuelle trin eller særlige afslutninger har alle betydning for den samlede pris. Adgangsforholdene på ejendommen kan også påvirke arbejdets omfang.
                </p>
                <p>
                  Søger du efter komposit terrasse pris, er det derfor værd at undersøge, hvad der faktisk indgår i den pris, du sammenligner. To terrasser med samme antal kvadratmeter kan kræve forskellig opbygning og arbejdstid.
                </p>
                <p className="text-white font-normal pt-1">
                  Hos Hovendals tager vi udgangspunkt i det konkrete projekt, så tilbuddet afspejler den terrasse, du ønsker at få bygget.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
                  <span>Ring {companyInfo.phone}</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white/5 border border-white/10 p-8 space-y-6">
                <h3 className="text-lg font-medium text-white uppercase tracking-wider border-b border-white/10 pb-4">
                  Faktorer der påvirker prisen
                </h3>
                <ul className="space-y-3 text-sm text-slate-300 font-light">
                  <li className="flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-check text-[#004FA4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span>Kvalitet og type af kompositbrædder</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-check text-[#004FA4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span>Underlagets beskaffenhed og jordbundsforhold</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-check text-[#004FA4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span>Opbygningens højde og eventuel niveaudeling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-check text-[#004FA4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span>Integrerede trin, ramper eller særlige hjørneafslutninger</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-sharp fa-solid fa-check text-[#004FA4] mt-1 shrink-0" aria-hidden="true"></i>
                    <span>Tilkøb af matchende hegn, låge eller afskærmning</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 5: Tænk kompositterrassen sammen med resten af uderummet */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6 mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Samlet helhedsløsning
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
              Tænk kompositterrassen sammen med resten af uderummet
            </h2>
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              En terrasse bliver ofte endnu bedre, når adgang, afskærmning og de øvrige områder omkring boligen tænkes sammen. Ønsker du en mere åben afgrænsning, kan et <Link to="/smedejernshegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">smedejernshegn</Link> være relevant. Ligger boligen tæt på trafik eller andre støjkilder, kan et <Link to="/stoejhegn" className="text-[#004FA4] font-medium underline hover:text-[#143463]">støjhegn</Link> også være værd at tænke ind omkring dele af uderummet.
            </p>
            <p className="text-base text-[#4B515D] font-light leading-relaxed">
              Adgangen kan tilpasses på samme måde. En <Link to="/smedejernslaage" className="text-[#004FA4] font-medium underline hover:text-[#143463]">smedejernslåge</Link> kan skabe en naturlig passage gennem et matchende hegn, mens en <Link to="/skydeporte" className="text-[#004FA4] font-medium underline hover:text-[#143463]">skydeport</Link> kan være en praktisk løsning ved en større indkørsel. Skal området ved boligen også rumme parkering under overdækning, kan en <Link to="/carport" className="text-[#004FA4] font-medium underline hover:text-[#143463]">carport</Link> tænkes sammen med terrasse, indkørsel og have. På den måde kan flere dele af uderummet planlægges med udgangspunkt i de samme behov.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/komposithegn"
              className="group p-6 border border-[#D6D3CE]/60 bg-[#F3F4F7] hover:bg-white hover:border-[#004FA4]/40 transition-all flex flex-col justify-between"
            >
              <div>
                <i className="fa-sharp fa-solid fa-fence text-[#004FA4] text-2xl mb-4 block" aria-hidden="true"></i>
                <h4 className="font-medium text-[#143463] text-base mb-2 group-hover:text-[#004FA4] transition-colors">
                  Komposithegn
                </h4>
                <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                  Fuldend udtrykket med et matchende vedligeholdelsesfrit komposithegn.
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] mt-6 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                Læs mere &rarr;
              </span>
            </Link>

            <Link
              to="/kompositlaage"
              className="group p-6 border border-[#D6D3CE]/60 bg-[#F3F4F7] hover:bg-white hover:border-[#004FA4]/40 transition-all flex flex-col justify-between"
            >
              <div>
                <i className="fa-sharp fa-solid fa-door-open text-[#004FA4] text-2xl mb-4 block" aria-hidden="true"></i>
                <h4 className="font-medium text-[#143463] text-base mb-2 group-hover:text-[#004FA4] transition-colors">
                  Kompositlåger
                </h4>
                <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                  Skab naturlig og elegant adgang til haven og terrassen.
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] mt-6 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                Læs mere &rarr;
              </span>
            </Link>

            <Link
              to="/skydeporte"
              className="group p-6 border border-[#D6D3CE]/60 bg-[#F3F4F7] hover:bg-white hover:border-[#004FA4]/40 transition-all flex flex-col justify-between"
            >
              <div>
                <i className="fa-sharp fa-solid fa-warehouse text-[#004FA4] text-2xl mb-4 block" aria-hidden="true"></i>
                <h4 className="font-medium text-[#143463] text-base mb-2 group-hover:text-[#004FA4] transition-colors">
                  Skydeporte
                </h4>
                <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                  Stilren og praktisk adgang ved indkørsel og ejendom.
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] mt-6 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                Læs mere &rarr;
              </span>
            </Link>

            <Link
              to="/carport"
              className="group p-6 border border-[#D6D3CE]/60 bg-[#F3F4F7] hover:bg-white hover:border-[#004FA4]/40 transition-all flex flex-col justify-between"
            >
              <div>
                <i className="fa-sharp fa-solid fa-car text-[#004FA4] text-2xl mb-4 block" aria-hidden="true"></i>
                <h4 className="font-medium text-[#143463] text-base mb-2 group-hover:text-[#004FA4] transition-colors">
                  Carport & Skur
                </h4>
                <p className="text-xs text-[#4B515D] font-light leading-relaxed">
                  Overdækning og opbevaring designet i harmoni med boligen.
                </p>
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4] mt-6 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                Læs mere &rarr;
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Sektion 6: Kompositterrasse på Fyn, i Odense og resten af Danmark */}
      <section className="py-20 md:py-24 bg-[#F3F4F7] border-y border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Landsdækkende montering
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight">
              Kompositterrasse på Fyn, i Odense og resten af Danmark
            </h2>
            <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
              <p>
                Hovendals holder til i Otterup nær Odense på Fyn, men vi udfører terrasseprojekter i hele Danmark. Vi hjælper med både planlægning, materialevalg og den praktiske opbygning, så du får en kompositterrasse, der er tilpasset forholdene på din ejendom.
              </p>
              <p>
                Har du allerede mål og idéer, eller er du stadig ved at undersøge mulighederne? <Link to="/kontakt" className="text-[#004FA4] font-medium underline hover:text-[#143463]">Fortæl os om projektet</Link>, så tager vi en snak om størrelse, materialer, opbygning og prisen på din kompositterrasse.
              </p>
            </div>
            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Fortæl os om dit projekt</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white border border-[#D6D3CE] hover:border-[#004FA4] text-[#143463] font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <i className="fa-sharp fa-solid fa-phone text-[#004FA4]" aria-hidden="true"></i>
                <span>Ring {companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Galleri sektion */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Inspiration & Projekter
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight mt-1">
                Gallerivisning af terrasser og uderum
              </h2>
            </div>
            <p className="text-xs text-[#4B515D]">
              Klik på et billede for at se det i fuld skærm
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                onClick={() => setLightboxIndex(idx)}
                className="group cursor-pointer border border-[#D6D3CE]/60 p-2 bg-white hover:border-[#004FA4]/40 transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                  <img
                    src={imageUrl(img.src, "width=600,quality=80,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[#143463]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                    <span className="w-10 h-10 bg-white/90 text-[#143463] flex items-center justify-center text-sm shadow">
                      <i className="fa-sharp fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#004FA4] block mb-1">
                    {img.category}
                  </span>
                  <h3 className="text-sm font-medium text-[#143463] leading-snug">
                    {img.title}
                  </h3>
                  <p className="text-xs text-[#4B515D] font-light mt-1 line-clamp-2">
                    {img.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <LightboxModal
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}

      {/* Quote / Indhent tilbud sektion */}
      <QuoteSection />
    </>
  );
}
